import { inject, Injectable, NgZone } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { fromEvent, merge, Subject, Subscription, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { JwtService } from '../jwt/jwt-service';
import { SessionTimeoutDialog } from './session-timeout-dialog/session-timeout-dialog';
// import * as CryptoJS from 'crypto-js';

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  ngZone = inject(NgZone);
  dialogService = inject(DialogService);
  jwtService = inject(JwtService);

  private userActivity$ = new Subject<void>();
  private TIMEOUT_MINUTES: number | null = null;
  isDialogOpen: boolean = false;
  private timeoutSub?: Subscription;
  private sessionExpiredSubject = new Subject<void>();
  sessionExpired$ = this.sessionExpiredSubject.asObservable();

  init() {
    this.TIMEOUT_MINUTES = this.getFromLocalStorage();
    // this.TIMEOUT_MINUTES = 0.1;    // FOR TEST
    if (!this.TIMEOUT_MINUTES) return;

    if (this.timeoutSub && !this.timeoutSub.closed) return;

    // Track user activity (mouse, keyboard, scroll, touch)
    const activityEvents$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'touchstart')
    );

    // Reset timer whenever user interacts
    activityEvents$.subscribe(() => {
      if (!this.isDialogOpen) this.userActivity$.next();
    });

    this.ngZone.runOutsideAngular(() => {
      this.timeoutSub?.unsubscribe();
      this.timeoutSub = this.userActivity$
        .pipe(
          switchMap(() =>
            timer(this.TIMEOUT_MINUTES! * 60 * 1000).pipe(
              tap(() => {
                this.ngZone.run(() => this.handleTimeout());
              })
            )
          )
        )
        .subscribe();
    });

    // Start timer initially
    this.userActivity$.next();
  }

  private handleTimeout() {
    this.isDialogOpen = true;

    const ref = this.dialogService.open(SessionTimeoutDialog, {
      width: '37.5rem',
      modal: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      }
    });

    ref.onClose.subscribe((result) => {
      this.isDialogOpen = false;

      if (result === 'timeout') {
        this.sessionExpiredSubject.next();
        return;
      }

      this.userActivity$.next();
    });
  }

  getFromLocalStorage() {
    const loginInfo = this.jwtService.getClaims();
    if (!loginInfo) return null;

    return Number(loginInfo.userinactivetimeout);
  }

  destroy() {
    this.timeoutSub?.unsubscribe();
    this.timeoutSub = undefined;
    this.isDialogOpen = false;
    this.userActivity$.complete(); // stop the subject
    this.userActivity$ = new Subject<void>(); // reset for next login
  }
}
